<script setup>
import {ref, computed, onMounted} from "vue";
import moment from "moment";
import { helpers, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import courseStatuses from "@/config/lists/courseStatuses";
import Autocomplete from "@/components/academy/forms/elements/Autocomplete.vue";
import { useAcademyStore, useListStore, useUserStore } from "@/stores";
import { formatDateToIsoString } from "@/helpers/helpers";
import { formatDateTimeLocal } from "../helpers/helpers";

const userStore = useUserStore();
const listStore = useListStore();
const academyStore = useAcademyStore();

const user = userStore.user;

const thematicAreas = computed(() => listStore.thematicAreas);
const courseTypes = computed(() => listStore.courseTypes);
const courseSections = computed(() => listStore.courseSections);
const branches = computed(() => listStore.branches);
const programs = computed(() => listStore.programs);

const formatDateTime = (dateStr, timeStr) => {
  const date = moment(dateStr);

  if (timeStr != null) {
    const [hours, minutes] = timeStr.split(":");

    date.set({
      hour: parseInt(hours),
      minute: parseInt(minutes),
      second: 0,
      millisecond: 0,
    });
  }

  const finalDateTime = date.toISOString();

  return finalDateTime;
};

const submitForm = async () => {
  const isValid = await v$.value.$validate();
  form.value.createdUserId = user.id;

  const data = form.value;

  if (startDate.value != null) {
    data.startDate = formatDateTime(startDate.value, startTime.value);
    data.startDate = formatDateTimeLocal(data.startDate);
  }

  if (endDate.value != null) {
    data.endDate = formatDateTime(endDate.value, endTime.value);
    data.endDate = formatDateTimeLocal(data.endDate);
  }

  data.registrationDate = formatDateToIsoString(data.registrationDate);

  if (isValid) {
    await academyStore.createCourse(data);
  }
};

const rules = {
  cycle: {
    required: helpers.withMessage("Cycle is required", required),
  },
  courseSection: {
    required: helpers.withMessage("Training Course is required", required),
  },
  thematicArea: {
    required: helpers.withMessage("Thematic Area is required", required),
  },
  courseType: {
    required: helpers.withMessage("Course Type is required", required),
  },
  duration: {
    required: helpers.withMessage("Duration is required", required),
  },
  registrationDate: {
    required: helpers.withMessage("Registration Date is required", required),
  },
};

const startTime = ref(null);
const startDate = ref(null);
const startTimeMenu = ref(false);

const endTime = ref(null);
const endDate = ref(null);
const endTimeMenu = ref(false);

const searchPreregistered = ref(null);
const searchRegistered = ref(null);
const preregisteredBeneficiaries = ref([]);
const registeredBeneficiaries = ref([]);

const isLoading = ref(false);

const onInput = async (val, type) => {
  if (val != null && val.length > 2) {
    isLoading.value = true;
    if (type === "preregistered") {
      preregisteredBeneficiaries.value = await listStore.fetchBeneficiaryIntakes({
        keyword: val,
      });
    } else {
      registeredBeneficiaries.value = await listStore.fetchBeneficiaryIntakes({
        keyword: val,
      });
    }

    isLoading.value = false;
  } else {
    preregisteredBeneficiaries.value = [];
    registeredBeneficiaries.value = [];
  }
};

const form = ref({
  cycle: null,
  courseSection: null,
  thematicArea: null,
  courseType: null,
  status: null,
  startDate: null,
  endDate: null,
  duration: null,
  projects: null,
  branch: null,
  location: null,
  tutor: null,
  registrationDate: null,
  preregisteredBeneficiaries: null,
  registeredBeneficiaries: null,
  fileList: []
});

const v$ = useVuelidate(rules, form);

const isRequired = (field) => {
  return "required" in rules[field];
};

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

onMounted(async () => {
  await listStore.fetchThematicAreas();
  await listStore.fetchCourseTypes();
  await listStore.fetchCourseSections();
  await listStore.fetchBranches();
  await listStore.fetchPrograms();
});
</script>

<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="6">
        <h1 class="text-academy">Add Course</h1>
      </v-col>
      <v-col cols="6" class="d-flex justify-end">
        <v-btn color="academy" size="large" @click="submitForm">
          <v-icon icon="mdi-plus" /> Save new entry
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-form class="custom-form mt-5 d-flex flex-column">
          <v-text-field
            v-model="form.cycle"
            name="cycle"
            :error-messages="getErrorMessages('cycle').value"
            hide-details="auto"
            @blur="v$.cycle.$touch()"
          >
            <template #label>
              Training Cycle (ID)
              <span v-if="isRequired('cycle')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-text-field>

          <v-select
            v-model="form.courseSection"
            :items="courseSections"
            item-title="name"
            item-value="id"
            return-object
            variant="outlined"
            hide-details="auto"
            :error-messages="getErrorMessages('courseSection').value"
            @blur="v$.courseSection.$touch()"
          >
            <template #label>
              Training Course
              <span v-if="isRequired('courseSection')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-select>

          <v-select
            v-model="form.thematicArea"
            :items="thematicAreas"
            item-title="name"
            item-value="id"
            return-object
            variant="outlined"
            hide-details="auto"
            :error-messages="getErrorMessages('thematicArea').value"
            @blur="v$.thematicArea.$touch()"
          >
            <template #label>
              Thematic Area
              <span v-if="isRequired('thematicArea')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-select>

          <v-select
            v-model="form.courseType"
            :items="courseTypes"
            item-title="name"
            item-value="id"
            return-object
            variant="outlined"
            hide-details="auto"
            :error-messages="getErrorMessages('courseType').value"
            @blur="v$.courseType.$touch()"
          >
            <template #label>
              Type
              <span v-if="isRequired('courseType')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-select>

          <v-select
            v-model="form.status"
            :items="courseStatuses"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> Status </template>
          </v-select>

          <div class="d-flex" style="gap: 10px">
            <v-date-input
              v-model="startDate"
              variant="outlined"
              prepend-icon=""
              placeholder="DD/MM/YYYY"
              hide-details="auto"
              prepend-inner-icon="$calendar"
            >
              <template #label> Start Date </template>
            </v-date-input>
            <v-text-field
              v-model="startTime"
              :active="startTimeMenu"
              :focused="startTimeMenu"
              label="Start Time"
              prepend-inner-icon="mdi-clock-time-four-outline"
              readonly
              hide-details="auto"
            >
              <v-dialog v-model="startTimeMenu" activator="parent" width="auto">
                <v-time-picker
                  v-if="startTimeMenu"
                  v-model="startTime"
                  scrollable
                  format="24hr"
                  @update:model-value="startTimeMenu = false"
                />
              </v-dialog>
            </v-text-field>
          </div>

          <div class="d-flex" style="gap: 10px">
            <v-date-input
              v-model="endDate"
              variant="outlined"
              prepend-icon=""
              placeholder="DD/MM/YYYY"
              hide-details="auto"
              prepend-inner-icon="$calendar"
            >
              <template #label> End Date </template>
            </v-date-input>
            <v-text-field
              v-model="endTime"
              :active="endTimeMenu"
              :focused="endTimeMenu"
              label="End Time"
              prepend-inner-icon="mdi-clock-time-four-outline"
              readonly
              hide-details="auto"
            >
              <v-dialog v-model="endTimeMenu" activator="parent" width="auto">
                <v-time-picker
                  v-if="endTimeMenu"
                  v-model="endTime"
                  scrollable
                  format="24hr"
                  @update:model-value="endTimeMenu = false"
                />
              </v-dialog>
            </v-text-field>
          </div>
          <v-number-input
            v-model="form.duration"
            :reverse="false"
            control-variant="default"
            hide-details="auto"
            variant="outlined"
            :error-messages="getErrorMessages('duration').value"
            @blur="v$.duration.$touch()"
          >
            <template #label>
              Duration
              <span v-if="isRequired('duration')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-number-input>

          <Autocomplete
            v-model="form.projects"
            label="Odyssea Programs"
            :items="programs"
            :multiple="true"
            :chips="true"
          />

          <Autocomplete
            v-model="form.branch"
            label="Odyssea Branch"
            :items="branches"
            :multiple="false"
            :chips="false"
          />

          <v-text-field
            v-model="form.location"
            name="location"
            hide-details="auto"
          >
            <template #label> Location </template>
          </v-text-field>

          <v-text-field v-model="form.tutor" name="tutor" hide-details="auto">
            <template #label> Tutor </template>
          </v-text-field>

          <v-autocomplete
            v-model="form.preregisteredBeneficiaries"
            v-model:search-input="searchPreregistered"
            multiple
            :loading="isLoading"
            chips
            closable-chips
            return-object
            clearable
            placeholder="Start typing to Search"
            item-title="name"
            item-value="id"
            :items="preregisteredBeneficiaries"
            variant="outlined"
            hide-details="auto"
            :menu-props="{ maxWidth: 0 }"
            @update:search="(val) => onInput(val, 'preregistered')"
          >
            <template #label> Beneficiaries Pre-Registration </template>
          </v-autocomplete>

          <v-autocomplete
            v-model="form.registeredBeneficiaries"
            v-model:search-input="searchRegistered"
            multiple
            :loading="isLoading"
            chips
            closable-chips
            return-object
            clearable
            placeholder="Start typing to Search"
            item-title="name"
            item-value="id"
            :items="registeredBeneficiaries"
            variant="outlined"
            hide-details="auto"
            :menu-props="{ maxWidth: 0 }"
            @update:search="(val) => onInput(val, 'registered')"
          >
            <template #label> Beneficiaries Registration </template>
          </v-autocomplete>

          <v-date-input
            v-model="form.registrationDate"
            variant="outlined"
            prepend-icon=""
            placeholder="DD/MM/YYYY"
            hide-details="auto"
            prepend-inner-icon="$calendar"
            :error-messages="getErrorMessages('registrationDate').value"
            @blur="v$.registrationDate.$touch()"
          >
            <template #label>
              Registration Date
              <span v-if="isRequired('registrationDate')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-date-input>

          <div>
            <v-file-upload
              v-model="form.fileList"
              multiple
              clearable
              show-size
              variant="outlined"
              density="compact"
              title="Upload Course files"
            />
          </div>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-academy));
}
.flex-50 {
  flex: 0 0 50%;
}
</style>
